import type { PagedRecord } from '../pages/recordPages';

/**
 * 🔒 Which flag a document's Company must carry (T184; decision 0637). The
 * product owner, 2026-09-22: *"Customer is company that sends us money for
 * service"* and *"Vendors are companies that charge for a good or service we
 * buy"*, so the kinds that bring money in name a customer and the kinds that
 * send it out name a vendor.
 *
 * 🔨 One fact per kind, read by the pickers and by every service that resolves
 * the Company. `Record<CounterpartyKind, …>` is what makes a new kind declare
 * its side rather than silently offering every company.
 */
export type Counterparty = 'customer' | 'vendor';

/** The ten kinds that name a counterparty: six on the customer side, four on the vendor side. */
export type CounterpartyKind = Extract<PagedRecord, 'Quote' | 'SalesOrder' | 'Invoice' | 'CreditNote' | 'WorkOrder' | 'Deal' | 'PurchaseOrder' | 'Bill' | 'VendorCredit' | 'RFQ'>;

export const COUNTERPARTY: Readonly<Record<CounterpartyKind, Counterparty>> = {
  Quote: 'customer',
  SalesOrder: 'customer',
  Invoice: 'customer',
  /* A credit given back to somebody who paid us (decision 0637, extending the owner's list). */
  CreditNote: 'customer',
  WorkOrder: 'customer',
  Deal: 'customer',
  PurchaseOrder: 'vendor',
  /* A bill, a vendor's credit and a request for a price all face somebody we buy from (decision 0637, extending the owner's list). */
  Bill: 'vendor',
  VendorCredit: 'vendor',
  RFQ: 'vendor',
};

/** How a refusal names the kind: the article belongs with the noun. */
const KIND_NOUN: Readonly<Record<CounterpartyKind, string>> = {
  Quote: 'a Quote',
  SalesOrder: 'a Sales Order',
  Invoice: 'an Invoice',
  CreditNote: 'a Credit Note',
  WorkOrder: 'a Work Order',
  Deal: 'a Deal',
  PurchaseOrder: 'a Purchase Order',
  Bill: 'a Bill',
  VendorCredit: 'a Vendor Credit',
  RFQ: 'an RFQ',
};

/** The query value the company list takes to offer one side alone. */
export function counterpartyQuery(kind: CounterpartyKind): string {
  return `role=${COUNTERPARTY[kind]}`;
}

/** Whether a string is one of the two sides, as the list route reads its `role` parameter. */
export function isCounterparty(value: unknown): value is Counterparty {
  return value === 'customer' || value === 'vendor';
}

/**
 * 🔒 The one refusal per side every service throws (decision 0637): the kind,
 * the flag it needs, and the Company by its display name. Null where the flag
 * is carried.
 */
export function counterpartyRefusal(kind: CounterpartyKind, company: { displayName: string; isCustomer: boolean; isVendor: boolean }): string | null {
  const side = COUNTERPARTY[kind];
  const carries = side === 'customer' ? company.isCustomer : company.isVendor;
  if (carries) return null;
  return `${capitalised(KIND_NOUN[kind])} names a Company marked as a ${side}; ${company.displayName} is not marked as a ${side}`;
}

function capitalised(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
